<script lang="ts" module>
  import {
    DefaultScheme,
    DefaultSize,
    type ComponentSize,
    type DaisyRoleScheme,
  } from '../styles.ts';

  const schemeToClass: Record<DaisyRoleScheme, string | undefined> = {
    p: 'btn-primary',
    s: 'btn-secondary',
    a: 'btn-accent',
    n: 'btn-neutral',
    in: 'btn-info',
    su: 'btn-success',
    wa: 'btn-warning',
    er: 'btn-error',
    def: undefined,
  };

  const sizeToClass: Record<ComponentSize, string | undefined> = {
    xs: 'btn-xs',
    sm: 'btn-sm',
    md: 'btn-md',
    lg: 'btn-lg',
    xl: 'btn-xl',
  };

  export type ButtonShape = 'default' | 'square' | 'circle';
  const shapeToClass: Record<ButtonShape, string | undefined> = {
    default: undefined,
    square: 'btn-square',
    circle: 'btn-circle',
  };

  export type ButtonLook = 'default' | 'soft' | 'outline' | 'dash' | 'active';
  const lookToClass: Record<ButtonLook, string | undefined> = {
    default: undefined,
    soft: 'btn-soft',
    outline: 'btn-outline',
    dash: 'btn-dash',
    active: 'btn-active',
  };

  type ElementType = HTMLButtonElement | HTMLInputElement | HTMLAnchorElement;
  export type ButtonClickEvent = MouseEvent & { currentTarget: EventTarget & ElementType };
  export type ButtonInputEvent = Event & { currentTarget: EventTarget & HTMLInputElement };

  export interface ButtonProps {
    // Identity
    id?: string;
    name?: string;
    el?: HTMLButtonElement | HTMLAnchorElement | HTMLInputElement;
    value?: string;
    tag?: 'button' | 'a' | 'input';
    // TODO: make these typesafe based on tag.
    type?: string;
    href?: string;
    download?: string;
    oninput?: (e: ButtonInputEvent) => void;
    min?: string;
    max?: string;

    // Behavior
    onClick?: (e: ButtonClickEvent) => void;
    role?: 'button' | 'submit' | 'reset' | 'dialog';
    disabled?: boolean | 'fake';

    // Appearance
    shape?: ButtonShape;
    look?: ButtonLook;
    scheme?: DaisyRoleScheme;
    size?: ComponentSize;
    class?: string;

    // Content
    children?: Snippet;
  }
</script>

<script lang="ts">
  import type { Snippet } from 'svelte';
  import { classes } from '../gui/util.ts';
  import type { FullOptional } from '@yp/common/identity.ts';

  let {
    id,
    name,
    el = $bindable(),
    value = name,
    tag = 'button',
    onClick,
    role = 'button',
    disabled: disabledState,
    shape = 'default',
    look = 'default',
    scheme = DefaultScheme,
    size = DefaultSize,
    class: propClass,
    children,
    ...extraProps
  }: FullOptional<ButtonProps> = $props();

  const type = $derived(role === 'dialog' ? 'submit' : role);
  const formmethod = $derived(role === 'dialog' ? 'dialog' : undefined);
  const disabled = $derived(disabledState === true ? true : undefined);

  const clazz = $derived(
    classes(
      'btn',
      shapeToClass[shape],
      lookToClass[look],
      schemeToClass[scheme],
      sizeToClass[size],
      disabledState === 'fake' ? 'btn-disabled !pointer-events-auto' : undefined,
      propClass,
    ),
  );
</script>

<!-- <button -->
<svelte:element
  this={tag}
  {id}
  {name}
  {value}
  bind:this={el}
  {type}
  {formmethod}
  class={clazz}
  onclick={(e: ButtonClickEvent) => onClick?.(e)}
  {disabled}
  {...extraProps}>{@render children?.()}</svelte:element>
<!-- </button> -->
